import { useEffect, useState } from "react";
import {
  DEFAULT_VALUE_FILTER,
  MAX_IGNORED_TERMS,
  MAX_TERM_LENGTH,
  PROVIDER_LABELS,
  type ChannelOverride,
  type ValueFilter,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { forwardingErrorMessage, normalizeTerm } from "@/lib/forwarding";
import {
  useDeleteChannelOverride,
  useSaveChannelOverride,
  type ForwardingChannel,
} from "@/lib/hooks/useForwarding";
import { Sheet, SheetSection } from "./Sheet";
import { ChipListInput } from "./ChipListInput";
import { ValueFilterFields } from "./ValueFilterFields";
import { InlineError, Toggle, selectClassName } from "./controls";

const PROVIDERS = ["amazon", "mercadolivre", "shopee", "magalu", "aliexpress", "shein", "awin"];

interface Draft {
  allowedProviders: string[] | null;
  ignoredTerms: string[];
  linkStyle: "short" | "direct" | null;
  valueFilter: ValueFilter | null;
}

function toDraft(override: ChannelOverride | null): Draft {
  return {
    allowedProviders: override?.allowedProviders ? [...override.allowedProviders] : null,
    ignoredTerms: override ? [...override.ignoredTerms] : [],
    linkStyle: override?.linkStyle ?? null,
    valueFilter: override?.valueFilter ?? null,
  };
}

interface ChannelOverrideSheetProps {
  readonly channel: ForwardingChannel | null;
  readonly override: ChannelOverride | null;
  readonly onClose: () => void;
}

function ProvidersPicker({
  value,
  onChange,
}: {
  value: string[] | null;
  onChange: (v: string[] | null) => void;
}): JSX.Element {
  const all = value === null;
  return (
    <div className="space-y-3">
      <Toggle
        id="ov-all-providers"
        checked={all}
        onChange={(c) => onChange(c ? null : [...PROVIDERS])}
        label="Converter todas as lojas"
      />
      {!all && (
        <div className="flex flex-wrap gap-1.5">
          {PROVIDERS.map((code) => {
            const on = value.includes(code);
            return (
              <button
                key={code}
                type="button"
                aria-pressed={on}
                onClick={() => onChange(on ? value.filter((c) => c !== code) : [...value, code])}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  on
                    ? "border-primary/40 bg-primary/15 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {PROVIDER_LABELS[code] ?? code}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type SetDraft = (patch: Partial<Draft>) => void;

function LinkStyleSection({ draft, set }: { draft: Draft; set: SetDraft }): JSX.Element {
  return (
    <SheetSection title="Link enviado">
      <select
        aria-label="Estilo do link desta origem"
        className={selectClassName}
        value={draft.linkStyle ?? ""}
        onChange={(e) =>
          set({
            linkStyle:
              e.target.value === "short" || e.target.value === "direct" ? e.target.value : null,
          })
        }
      >
        <option value="">Usar o padrão global</option>
        <option value="short">Link curto LucraLink</option>
        <option value="direct">Afiliado direto</option>
      </select>
    </SheetSection>
  );
}

function OwnValueFilterSection({ draft, set }: { draft: Draft; set: SetDraft }): JSX.Element {
  return (
    <SheetSection title="Filtro por valor">
      <Toggle
        id="ov-own-vf"
        checked={draft.valueFilter !== null}
        onChange={(own) =>
          set({ valueFilter: own ? { ...DEFAULT_VALUE_FILTER, enabled: true } : null })
        }
        label="Usar um filtro próprio nesta origem"
      />
      {draft.valueFilter && (
        <ValueFilterFields
          idPrefix="ov-vf"
          value={draft.valueFilter}
          onChange={(valueFilter) => set({ valueFilter })}
        />
      )}
    </SheetSection>
  );
}

function OverrideFields({ draft, set }: { draft: Draft; set: SetDraft }): JSX.Element {
  return (
    <>
      <SheetSection title="Lojas que converte">
        <ProvidersPicker
          value={draft.allowedProviders}
          onChange={(allowedProviders) => set({ allowedProviders })}
        />
      </SheetSection>
      <SheetSection title="Termos extras a ignorar" hint="Somados aos termos do padrão global.">
        <ChipListInput
          id="ov-terms"
          label="Termos"
          values={draft.ignoredTerms}
          max={MAX_IGNORED_TERMS}
          placeholder="ex.: sorteio"
          normalize={normalizeTerm}
          errorText={`Cada termo pode ter até ${MAX_TERM_LENGTH} caracteres.`}
          onChange={(ignoredTerms) => set({ ignoredTerms })}
        />
      </SheetSection>
      <LinkStyleSection draft={draft} set={set} />
      <OwnValueFilterSection draft={draft} set={set} />
    </>
  );
}

function OverrideFooter({
  canReset,
  resetting,
  saving,
  onReset,
  onSave,
}: {
  canReset: boolean;
  resetting: boolean;
  saving: boolean;
  onReset: () => void;
  onSave: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <Button variant="ghost" disabled={!canReset || resetting} onClick={onReset}>
        Voltar ao padrão global
      </Button>
      <Button disabled={saving} onClick={onSave}>
        {saving ? "Salvando..." : "Salvar ajustes"}
      </Button>
    </div>
  );
}

/** Ajustes de UMA origem: lojas convertidas, termos extras, link e filtro por valor. */
export function ChannelOverrideSheet({
  channel,
  override,
  onClose,
}: ChannelOverrideSheetProps): JSX.Element | null {
  const [draft, setDraft] = useState<Draft>(toDraft(override));
  const [error, setError] = useState<string | null>(null);
  const save = useSaveChannelOverride();
  const remove = useDeleteChannelOverride();

  useEffect(() => {
    setDraft(toDraft(override));
    setError(null);
  }, [channel?.id, override]);

  if (!channel) return null;
  const set = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));
  const run = async (action: () => Promise<void>): Promise<void> => {
    try {
      await action();
      onClose();
    } catch (err) {
      setError(forwardingErrorMessage(err));
    }
  };
  const key = { platform: channel.platform, channelRef: channel.ref };

  return (
    <Sheet
      open
      title={`Ajustes de ${channel.title ?? channel.ref}`}
      description="Herda o padrão global e só muda o que você definir aqui."
      onClose={onClose}
      footer={
        <OverrideFooter
          canReset={override !== null}
          resetting={remove.isPending}
          saving={save.isPending}
          onReset={() => void run(() => remove.mutateAsync(key))}
          onSave={() => void run(() => save.mutateAsync({ ...key, ...draft }))}
        />
      }
    >
      <InlineError message={error} />
      <OverrideFields draft={draft} set={set} />
    </Sheet>
  );
}
